/*
===============================================================================================================
                                 Copyright 2024-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-console */
/**
 * Abstraction from telemetry implementation. Connects up to amplitude, can be used to enable more/different
 * telemetry methods in the future without needing any code changes outside of this directory.
 */
import sentryLogger from '@/utilities/telemetry/sentry';
import appInsights from '@/utilities/telemetry/app-insights';
import amplitude from './amplitude';

const shouldUseTelemetry = process.env.NODE_ENV === 'production';

/**
 * Called on app load to initialise the telemetry instance.
 * Doesn't do anything in dev mode as then we will only output events to the console
 *
 */
const initTelemetry = (apiKey) => {
  if (apiKey && shouldUseTelemetry) {
    amplitude.init(apiKey);
  }
};

/**
 * Send a telemetry event.
 * Note that in dev mode we just log the event out to the console
 *
 * @param {string} eventName - The name of the event that we wish to send
 * @param {object} eventProperties - Object containing all of the event information that we wish to send
 * @param {object} options - Object containing configuration options
 */
const sendEvent = (eventName, eventProperties, { hasApiKey } = {}) => {
  if (shouldUseTelemetry && hasApiKey) {
    amplitude.sendEvent(eventName, eventProperties);
  } else {
    console.log('%c [Event: %s] [Properties: %o]', 'color: #00B7F1', eventName, eventProperties);
  }
};

/**
 * Log an exception to the telemetry service.
 * @param {object} err - The error object to log
 */
const logException = (err) => {
  if (sentryLogger.isEnabled) {
    sentryLogger.logException(err);
  }
  if (appInsights.isEnabled) {
    appInsights.logException(err);
  }
};

/**
 * Log a custom event to the telemetry service.
 * @param {object} eventPayload - The event payload to log
 */
const logEvent = (eventPayload) => {
  if (sentryLogger.isEnabled) {
    sentryLogger.logEvent(eventPayload);
  }
  if (appInsights.isEnabled) {
    appInsights.logEvent(eventPayload);
  }
};

const flush = () => {
  if (sentryLogger.isEnabled) {
    sentryLogger.flush();
  }
  if (appInsights.isEnabled) {
    appInsights.flush();
  }
};

const trackPageView = (router) => {
  if (appInsights.isEnabled) {
    appInsights.trackPageView(router);
  }
};

export default {
  initTelemetry,
  sendEvent,
  logException,
  logEvent,
  trackPageView,
  flush,
};
